import React, { useState, useCallback, useMemo } from 'react'
import { Button, IconButton, Popover } from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { ArrowForwardIos, Close } from '@mui/icons-material'
import { Medication } from 'types/treatments'

const statusList = ['pending', 'ongoing', 'completed', 'discontinued'] as const

interface FilterProps {
  filterByStatus: (status: string | null) => void
  status: Medication['medication_status'] | undefined
}

interface FilterState {
  status: Medication['medication_status'] | null
}

export default function Filter(props: FilterProps) {
  const { filterByStatus } = props

  const [filters, setFilters] = useState<FilterState>({
    status: props.status || null,
  })

  const { status } = filters
  const [openFilterBox, setOpenFilterBox] = useState<HTMLButtonElement | null>(null)
  const [statusFilterOpen, setStatusFilterOpen] = useState<HTMLButtonElement | null>(null)

  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    setOpenFilterBox(event.currentTarget)
  }, [])

  const resetFilter = useCallback(() => {
    setFilters({
      status: null,
    })
    filterByStatus(null)
    setOpenFilterBox(null)
  }, [filterByStatus])

  const filterFunction = useCallback((name: 'status', item: any) => {
    const newFilters = {
      status: null,
    }
    newFilters[name] = item
    setFilters(newFilters)
  }, [])

  const label = useMemo(() => {
    if (status) {
      return <div className="bg-brand-50 text-brand-700 rounded-lg p-2 h-fit">{status}</div>
    }
    return 'Filter'
  }, [status])

  return (
    <>
      <div className="flex items-center">
        <Button
          startIcon={<FilterListIcon />}
          variant="outlined"
          endIcon={<KeyboardArrowDownIcon />}
          className="px-3 border border-default text-neutral-500 w-fit h-fit py-3"
          onClick={handleClick}
        >
          {label}
        </Button>
        {status && (
          <IconButton
            className="w-fit h-fit ml-2"
            onClick={resetFilter}
            aria-label="Clear applied filter"
          >
            <Close />
          </IconButton>
        )}
      </div>

      <Popover
        open={Boolean(openFilterBox)}
        anchorEl={openFilterBox}
        onClose={() => setOpenFilterBox(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <div className="rounded-xl bg-white w-full flex flex-col justify-center items-start rounded-10 bg-neutral-color-white shadow-default">
          <Button
            fullWidth
            size="small"
            onClick={(e) => {
              setStatusFilterOpen(e.currentTarget)
            }}
            endIcon={<ArrowForwardIos className="text-xs" />}
            variant="text"
            className="hover:bg-white text-neutral-500 h-fit justify-start px-4"
          >
            Status
          </Button>
        </div>
      </Popover>

      <Popover
        open={Boolean(statusFilterOpen)}
        anchorEl={statusFilterOpen}
        onClose={() => {
          setStatusFilterOpen(null)
        }}
      >
        <div className="rounded-xl overflow-auto gap-2 p-2 bg-white flex flex-col justify-center items-start rounded-10 shadow-default">
          {statusList?.map((item) => (
            <Button
              fullWidth
              key={item}
              className="text-neutral-500 border border-neutral-100 h-fit"
              variant="outlined"
              size="large"
              onClick={() => {
                resetFilter()
                filterFunction('status', item)
                filterByStatus(item)
              }}
            >
              {item}
            </Button>
          ))}
        </div>
      </Popover>
    </>
  )
}
